<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-md-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
                            <div class="d-flex align-items-center user-member__title justify-content-center mr-sm-25">
                                <h4 class="text-capitalize fw-500 breadcrumb-title">
                                    Node Server Monitoring
                                </h4>
                            </div>
                        </div>
                        <div class="action-btn">
                            <button @click="refreshStatus" class="btn px-15 btn-secondary" :class="{ 'btn-loading': loading }" style="display: inline-flex; margin: 5px">
                                <i class="fas fa-sync-alt fs-16"></i>
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Status Summary Cards -->
            <div class="row mb-30">
                <div class="col-md-3">
                    <div class="metric-card bg-white radius-xl p-20 border">
                        <div class="metric-card__content">
                            <p class="metric-card__title text-light-gray fs-14 text-uppercase">Total Servers</p>
                            <h3 class="metric-card__count fw-500">{{ totalServers }}</h3>
                        </div>
                        <div class="metric-card__icon text-primary">
                            <i class="fas fa-server"></i>
                        </div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="metric-card bg-white radius-xl p-20 border">
                        <div class="metric-card__content">
                            <p class="metric-card__title text-light-gray fs-14 text-uppercase">Online</p>
                            <h3 class="metric-card__count fw-500 text-success">{{ onlineServers }}</h3>
                        </div>
                        <div class="metric-card__icon text-success">
                            <i class="fas fa-check-circle"></i>
                        </div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="metric-card bg-white radius-xl p-20 border">
                        <div class="metric-card__content">
                            <p class="metric-card__title text-light-gray fs-14 text-uppercase">Offline</p>
                            <h3 class="metric-card__count fw-500 text-danger">{{ offlineServers }}</h3>
                        </div>
                        <div class="metric-card__icon text-danger">
                            <i class="fas fa-times-circle"></i>
                        </div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="metric-card bg-white radius-xl p-20 border">
                        <div class="metric-card__content">
                            <p class="metric-card__title text-light-gray fs-14 text-uppercase">Monitoring</p>
                            <h3 class="metric-card__count fw-500" :class="monitorRunning ? 'text-success' : 'text-danger'">
                                {{ monitorRunning ? 'Active' : 'Inactive' }}
                            </h3>
                        </div>
                        <div class="metric-card__icon" :class="monitorRunning ? 'text-success' : 'text-danger'">
                            <i class="fas fa-clock"></i>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-lg-12">
                    <div class="userDatatable global-shadow border p-30 bg-white radius-xl w-100 mb-30">
                        <div class="table-responsive">
                            <table class="table mb-0 table-hover">
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Server URL</th>
                                        <th>Response Time</th>
                                        <th>Last Online</th>
                                        <th>Last Offline</th>
                                        <th>Last Checked</th>
                                        <th class="text-center">Terkoneksi</th>
                                        <th class="text-center">Putus / belum</th>
                                        <th class="text-center" title="Counter di baris node server">Total*</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="server in servers" :key="server.id">
                                        <td>
                                            <span class="badge" :class="getStatusBadgeClass(server.status)">
                                                {{ server.status || 'unknown' }}
                                            </span>
                                        </td>
                                        <td>
                                            <span class="server-url">{{ server.url }}</span>
                                        </td>
                                        <td>
                                            <span :class="getResponseTimeClass(server.response_time)">
                                                {{ server.response_time ? server.response_time + 'ms' : '-' }}
                                            </span>
                                        </td>
                                        <td>
                                            {{ formatTimestamp(server.last_online_at) }}
                                        </td>
                                        <td>
                                            {{ formatTimestamp(server.last_offline_at) }}
                                        </td>
                                        <td>
                                            {{ formatTimestamp(server.last_checked_at) }}
                                        </td>
                                        <td class="text-center">
                                            <span class="badge badge-success">{{ formatInt(server.connected_count) }}</span>
                                        </td>
                                        <td class="text-center">
                                            <span class="badge badge-danger">{{ formatInt(server.disconnected_count) }}</span>
                                        </td>
                                        <td class="text-center">
                                            <span class="badge badge-secondary" :title="deviceTotalHint(server)">{{ formatInt(server.device_count) }}</span>
                                        </td>
                                    </tr>
                                    <tr v-if="servers.length === 0 && !loading">
                                        <td colspan="9" class="text-center py-4">
                                            No node servers found
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p class="small text-muted mb-0 mt-2">
                            Terkoneksi / putus dihitung dari status device di database (sama seperti menu Koneksi Device).
                            Kolom Total* = counter <code>device_count</code> pada node.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        data() {
            return {
                servers: [],
                loading: false,
                monitorRunning: false,
                monitorInterval: 5,
            };
        },
        computed: {
            totalServers() {
                return this.servers.length;
            },
            onlineServers() {
                return this.servers.filter(s => s.status === 'online').length;
            },
            offlineServers() {
                return this.servers.filter(s => s.status === 'offline').length;
            },
        },
        mounted() {
            this.loadServerStatus();
            // Auto-refresh every 30 seconds
            this.autoRefresh = setInterval(() => {
                this.loadServerStatus();
            }, 30000);
        },
        beforeDestroy() {
            if (this.autoRefresh) {
                clearInterval(this.autoRefresh);
            }
        },
        methods: {
            async loadServerStatus() {
                this.loading = true;
                try {
                    const response = await this.$store.dispatch('admin_node_servers_status');
                    this.servers = response.data.data.node_servers || [];
                    this.monitorRunning = response.data.data.monitor_running || false;
                    this.monitorInterval = response.data.data.monitor_interval_minutes || 5;
                } catch (err) {
                    this.$toast.error('Failed to load server status');
                    console.error(err);
                }
                this.loading = false;
            },
            async refreshStatus() {
                await this.loadServerStatus();
                this.$toast.success('Status refreshed');
            },
            getStatusBadgeClass(status) {
                if (status === 'online') return 'badge-success';
                if (status === 'offline') return 'badge-danger';
                return 'badge-secondary';
            },
            getResponseTimeClass(time) {
                if (!time) return 'text-muted';
                if (time < 500) return 'text-success';
                if (time < 1000) return 'text-warning';
                return 'text-danger';
            },
            formatInt(n) {
                const v = Number(n);
                if (!Number.isFinite(v)) return '0';
                return v.toLocaleString('id-ID');
            },
            deviceTotalHint(server) {
                const live = Number(server.connected_count || 0) + Number(server.disconnected_count || 0);
                const counter = Number(server.device_count || 0);
                if (live !== counter) {
                    return `Device di DB: ${live} · counter node: ${counter}`;
                }
                return `Total device di node: ${live}`;
            },
            formatTimestamp(timestamp) {
                if (!timestamp) return '-';
                const date = new Date(timestamp);
                const now = new Date();
                const diff = now - date;

                // If less than 1 minute
                if (diff < 60000) {
                    return 'Just now';
                }
                // If less than 1 hour
                if (diff < 3600000) {
                    const mins = Math.floor(diff / 60000);
                    return `${mins} min${mins > 1 ? 's' : ''} ago`;
                }
                // If less than 24 hours
                if (diff < 86400000) {
                    const hours = Math.floor(diff / 3600000);
                    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
                }
                // Otherwise show date
                return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            },
        },
    };
</script>

<style scoped>
    .metric-card {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .metric-card__content {
        flex: 1;
    }

    .metric-card__icon {
        font-size: 2rem;
        opacity: 0.3;
    }

    .metric-card__count {
        font-size: 1.75rem;
        margin: 0;
    }

    .metric-card__title {
        margin: 0 0 5px 0;
    }

    .server-url {
        font-family: monospace;
        font-size: 0.85rem;
        color: #666;
    }

    .badge-success {
        background-color: #00c853;
        color: white;
    }

    .badge-danger {
        background-color: #ff1744;
        color: white;
    }

    .badge-secondary {
        background-color: #6c757d;
        color: white;
    }

    .text-success {
        color: #00c853 !important;
    }

    .text-danger {
        color: #ff1744 !important;
    }

    .text-warning {
        color: #ff9100 !important;
    }

    .text-muted {
        color: #9e9e9e !important;
    }

    .btn-loading {
        pointer-events: none;
        opacity: 0.7;
    }
</style>